/**
 * Sorting options for the visits list, shared by the visits statistics page
 * (/statistics/visits) and the client card's visit history tab. Both screens render the
 * very same VisitCard, so both offer the very same sorting — keeping the list in one
 * place is what stops the two selects from drifting apart.
 *
 * The `value` is `<column>-<direction>`, split on the dash by the caller and sent to the
 * backend as orders[0][column] / orders[0][direction]. Columns must stay in sync with the
 * backend whitelists: Backend\Validator\Statistics\VisitsStatsRequestDTO and
 * Backend\Validator\Client\GetVisitsAndSalesRequestDTO (getAllowedSortColumns()).
 *
 * @param {Function} t vue-i18n translate function
 * @param {{ withClient?: boolean }} [opts] client sorting only makes sense where more than
 *        one client is listed, so the client history leaves those two options out
 * @returns {Array<{ title: string, value: string, icon: string }>}
 */
export const buildVisitSortingOptions = (t, { withClient = false } = {}) => [
  { title: t('default_sort_newest'), value: 'date-desc', icon: 'ri-sort-desc' },
  { title: t('oldest'), value: 'date-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_employee_asc'), value: 'employee-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_employee_desc'), value: 'employee-desc', icon: 'ri-sort-desc' },
  ...(withClient
    ? [
      { title: t('visits_stats_tab.sort_client_asc'), value: 'client-asc', icon: 'ri-sort-asc' },
      { title: t('visits_stats_tab.sort_client_desc'), value: 'client-desc', icon: 'ri-sort-desc' },
    ]
    : []),
  { title: t('visits_stats_tab.sort_paid_asc'), value: 'finalPrice-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_paid_desc'), value: 'finalPrice-desc', icon: 'ri-sort-desc' },
  { title: t('visits_stats_tab.sort_tip_asc'), value: 'tip-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_tip_desc'), value: 'tip-desc', icon: 'ri-sort-desc' },
  { title: t('visits_stats_tab.sort_gained_points_asc'), value: 'gainedPoints-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_gained_points_desc'), value: 'gainedPoints-desc', icon: 'ri-sort-desc' },
  { title: t('visits_stats_tab.sort_spent_points_asc'), value: 'spentPoints-asc', icon: 'ri-sort-asc' },
  { title: t('visits_stats_tab.sort_spent_points_desc'), value: 'spentPoints-desc', icon: 'ri-sort-desc' },
]

/**
 * Every column the selects above can ask for. Used to drop a stale sorting read back from
 * localStorage instead of sending it to the backend, which rejects an unknown column with 400.
 */
export const VISIT_SORT_COLUMNS = [
  'date',
  'employee',
  'client',
  'finalPrice',
  'tip',
  'gainedPoints',
  'spentPoints',
]
